import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import bus from 'lib/bus';
import { getBreakpoint, getValueForTag, decodeObjectID } from 'utils/general';
import * as queries from 'lib/graphql/queries';
import storefront from 'lib/storefront';
import {
  trackProductFindShade,
  trackTryOn,
  trackFindMyShade,
  trackTryShadeOn,
} from 'lib/tracking';
import klaviyo from "lib/klaviyo";

const isDesktopLayout = () => {
  return ['md', 'l'].includes(getBreakpoint());
};

/**
 *
 * Product Gallery component
 */
export default {
  components: {
    'vue-image': () => import('../vue-image/vue-image.vue'),
    'vue-image-v2': () => import('../vue-image-v2/vue-image-v2.vue'),
    'vue-image-zoom': () => import('../image-zoom/vue-image-zoom.vue')
  },
  props: [
    'collectionAwards'
  ],
  data() {
    return {
      thumbScrollIndex: 0,
      hasScrollButtons: false,
      isTryCombination: false,
      countInterval: null,
      parentProductId: null,
      tagText: 'Shimmer',
      tagText2: 'Matte',
      productTitle: 'Try solo',
      secondaryProductTitle: '',
      shades: window.__GLOBAL__.shades.values,
      circleBackgroundColor1: '#fff',
      circleBackgroundColor2: '#fff',
      campaignType: 'solo',
      showingCombo: false,
      btnCount: 0,
      player: null,
      isPlaying: false,
      isMuted: true,
      patternObject: {
        0: { color1: '7462199754797', color2: '7459287957549' },
        1: { color1: '7462199820333', color2: '7459321446445' },
        2: { color1: '7462200016941', color2: '7459327901741' },
        3: { color1: '7462200148013', color2: '7459359457325' },
        4: { color1: '7462200279085', color2: '7459346579501' },
        5: { color1: '7462200344621', color2: '7462193954861' },
        6: { color1: '7462200410157', color2: '7462193201197' },
        7: { color1: '7462200573997', color2: '7462199689261' },
      },
    };
  },
  created() {
    bus.$on('update-variant', this.handleVariantChange);
    window.addEventListener('resize', this.setThumbBarHeight);
  },
  destroyed() {
    window.removeEventListener('resize', this.setThumbBarHeight);
  },
  mounted() {
    this.setThumbBarHeight();
    //this.initVimeoPlayer();
    document.addEventListener('DOMContentLoaded', () => {
      this.initVimeoPlayer();
    });

    if (!this.$refs.thumbItems.style.top) {
      this.$refs.thumbItems.style.top = 0;
    }
  },
  watch: {
    async product() {
      if (this.showingCombo) {
        this.handleCombinationChange(this.product.id);
      } else {
        await this.getParentProductId();
        if (window.YMK) window.YMK.applyMakeupBySku(this.parentProductId);
        const colorIdTag = this.product.tags?.find((t) =>
          t.includes('Color ID'),
        );
        const colorId = colorIdTag?.split('|')[1];
        const shade = this.shades.find((shade) => shade.id === colorId);
        this.tagText =
          this.product?.metafields?.swatch_management?.additional_categories;
        this.productTitle = this.product?.variants[0]?.title;
        this.circleBackgroundColor1 = shade?.hex;
      }
    },
  },
  computed: {
    ...mapState('product', ['loading', 'product', 'metafields', 'activeImage', 'showingTryShade']),
    ...mapGetters('product', ['productTitle']),
    featured() {
      if (!this.product?.media.length) return false;
      let featured = this.product.media[this.activeImage];

      if (!Boolean(featured)) {
        this.setIndex(0);
        featured = this.product.media[0];
      }

      if (featured.type === 'video') {
        const url =
          featured.host === 'YOUTUBE'
            ? `https://www.youtube.com/embed/${featured.url
                .split('/')
                .pop()}?enablejsapi=1`
            : `https://player.vimeo.com/video/${featured.url
                .split('/')
                .pop()}?background=0&autoplay=1&loop=1&muted=1&portrait=0&controls=0`;
        return {
          type: 'video',
          host: featured.host,
          url,
          ...featured.previewImage,
        };
      }
      return featured;
    },
    scrollThumbsDownActive() {
      const remainingThumbs = this.product.media.length - this.thumbScrollIndex;
      return remainingThumbs * 140.5 > parseInt(this.$refs.thumbs.style.height);
    },
    scrollThumbsUpActive() {
      return !!this.thumbScrollIndex;
    },
    isSkinProduct() {
      return this.product.vendor === 'Fenty Skin';
    },
    showFindShadeButton() {
      if (!this.metafields) {
        return false;
      }
      return ![true, 'true'].includes(
        this.metafields?.general?.hide_shade_buttons,
      );
    },
    showTryShadeOnButton() {
      if (!this.metafields || window.__GLOBAL__.disableVto) {
        return false;
      }
      return ![true, 'true'].includes(
        this.metafields?.general?.hide_try_shade_on_button,
      );
    },
    isShadowStix() {
      return this.product.tags?.includes('Shadowstix');
    },
    productAwards() {
      return this.collectionAwards && this.collectionAwards.length ? this.collectionAwards : this.product.awards ? this.product.awards : [];
    }
  },
  methods: {
    ...mapActions('product', ['updateActiveImage']),
    ...mapActions('global', ['openModal', 'setCurrentProductGalleryVideo']),
    ...mapMutations('product', ['setShowingTryShade']),
    handleGalleryTracking() {
      dataLayer.push({
        event: 'product_module_interaction',
        click_action: 'click',
        product_name: this.productTitle,
        module_name: 'product gallery',
        shopify_module_name: 'product gallery'
      });
    },
    bindTracking() {
      window.YMK.addEventListener('reset', () => {
        trackTryShadeOn({ action: 'try_shade_on_reset', shopify_module_name: 'product gallery' });
      });

      window.YMK.addEventListener('compare', () => {
        trackTryShadeOn({ action: 'try_shade_on_compare', shopify_module_name: 'product gallery' });
      });

      window.YMK.addEventListener('compareDisabled', () => {
        trackTryShadeOn({ action: 'try_shade_on_compare_disable', shopify_module_name: 'product gallery' });
      });

      window.YMK.addEventListener('photoSaved', () => {
        trackTryShadeOn({ action: 'try_shade_on_save_photo', shopify_module_name: 'product gallery' });
      });
    },
    handleVariantChange(variant) {
      const variantImageIndex = this.product.media.findIndex(
        (image) => image.src === variant.image.src,
      );
      if (variantImageIndex > -1) {
        this.setIndex(variantImageIndex);
      }
    },
    initVimeoPlayer() {
      if (!this.$refs.vimeoPlayer) return;
      const interval = setInterval(() => {
        if (window.Vimeo) {
          clearInterval(interval);
          this.player = new Vimeo.Player(this.$refs.vimeoPlayer);
          this.player.ready().then(() => {
            this.togglePlay();
            this.toggleMute();
          });
        }
      }, 100);
    },
    onIframeLoaded() {
      this.initVimeoPlayer();
    },
    togglePlay() {
      if (this.player) {
        this.player.getPaused().then((paused) => {
          if (paused) {
            this.player.play();
            this.isPlaying = true;
          } else {
            this.player.pause();
            this.isPlaying = false;
          }
        });
      }
    },
    toggleMute() {
      if (this.player) {
        this.player.getMuted().then((muted) => {
          if (muted) {
            this.player.setMuted(false);
            this.isMuted = false;
          } else {
            this.player.setMuted(true);
            this.isMuted = true;
          }
        });
      }
    },
    openFeaturedModal() {
      if (this.featured.type === 'image' || this.featured.host === 'VIMEO') {
        return;
      }
      this.setCurrentProductGalleryVideo(this.featured);
      this.openModal('videoModal');
    },
    goToShadeFinder() {
      trackFindMyShade({ action: 'find_my_shade_initiate', shopify_module_name: 'product gallery' });
      trackProductFindShade(this.product);
      const item = this.product;
      window.klaviyo.push(['track', 'Shade finder recommended product', item]);
      location.href = __GLOBAL__.shades.finderHandle;
    },
    async getParentProductId() {
      if (!Object.hasOwn(this.product, 'title')) return;
      // If the product is a mini, try to query for the parent/full size product
      if (this.product.title.toLowerCase().includes('mini')) {
        const productHandle = this.product.handle.replace('-mini', '');
        const response = await storefront.request(queries.product, {
          handle: productHandle,
        });
        if (response?.product?.id) {
          this.parentProductId = decodeObjectID(response.product.id);
        } else {
          // There may not be a full-sized version, default to the regular version
          this.parentProductId = this.product.id;
        }
      } else {
        this.parentProductId = this.product.id;
      }
    },
    async getShadowstixProductById(id) {
      const response = await storefront.request(
        queries.getShadowstixProductById,
        {
          id: 'gid://shopify/Product/' + id,
        },
      );
      return response;
    },
    setThumbBarHeight() {
      if (isDesktopLayout()) {
        this.countInterval = setInterval(() => {
          if (this.$refs.thumbs) {
            this.$refs.thumbs.style.height = this.$refs.featured ? `${this.$refs.featured?.clientHeight}px` : 0;
          }
          if (
            this.$refs.thumbItems?.clientHeight >=
            this.$refs.featured?.clientHeight
          ) {
            this.hasScrollButtons = true;
          } else {
            this.hasScrollButtons = false;
          }

          this.btnCount += 1;
          if (this.btnCount > 3) {
            clearInterval(this.countInterval);
            this.btnCount = 0;
          }
        }, 1000);
      }
    },
    setIndex(index) {
      this.updateActiveImage(index);
      this.handleGalleryTracking();
    },
    async setTryCombination() {
      const { mainColorId, secondaryColorId } = await this.getComboProductsIds(
        this.product.id,
      );

      this.loadMakeupByPattern(mainColorId, secondaryColorId, true);
    },
    async handleCombinationChange(id) {
      if (this.isShadowStix && this.campaignType === 'combo') {
        const { mainColorId, secondaryColorId } =
          await this.getComboProductsIds(id);

        this.loadMakeupByPattern(mainColorId, secondaryColorId, false);
      }
    },
    loadMakeupByPattern(mainColorId, secondaryColorId, isInitialLoad) {
      setTimeout(() => {
        window.YMK.applyMakeupByPattern('2 colors99', [
          { itemGuid: mainColorId, colorIndex: 0, colorIntensity: 78 },
          {
            itemGuid: secondaryColorId,
            colorIndex: 0,
            colorIntensity: 78,
          },
        ]);
        trackTryOn(this.product);
        trackTryShadeOn({ action: 'try_shade_on_open', shopify_module_name: 'product gallery' });
      }, 2000);

      this.showingCombo = true;
      if (isInitialLoad) {
        this.setShowingTryShade(true)
        this.isTryCombination = true;
      }
    },
    async isShimmer(id) {
      const { product } = await this.getShadowstixProductById(id);
      if (!product) return;
      return product?.shadeDetails?.value.toLowerCase() === 'shimmer';
    },
    async setLeftShadeButton(id) {
      const { product } = await this.getShadowstixProductById(id);

      if (product) {
        const colorIdTag = product.tags?.find((t) => t.includes('Color ID'));
        const colorId = colorIdTag?.split('|')[1];
        const shade = this.shades.find((shade) => shade.id === colorId);
        this.tagText = product?.shadeDetails?.value;
        this.circleBackgroundColor1 = shade?.hex;
        this.productTitle = product?.variants.edges[0]?.node.title;
      }
    },
    async setRightShadeButton(id) {
      const { product } = await this.getShadowstixProductById(id);

      if (product) {
        const colorIdTag = product.tags?.find((t) => t.includes('Color ID'));
        const colorId = colorIdTag?.split('|')[1];
        const shade = this.shades.find((shade) => shade.id === colorId);
        this.tagText2 = product?.shadeDetails?.value;
        this.circleBackgroundColor2 = shade?.hex;
        this.secondaryProductTitle = product?.variants.edges[0]?.node.title;
      }
    },
    async setProductData(mainColorId, secondaryColorId) {
      const mainIsShimmer = await this.isShimmer(mainColorId);

      if (mainIsShimmer) {
        this.setLeftShadeButton(mainColorId);
        this.setRightShadeButton(secondaryColorId);
      } else {
        this.setLeftShadeButton(secondaryColorId);
        this.setRightShadeButton(mainColorId);
      }
    },
    async getComboProductsIds(id) {
      let mainColorId;
      let secondaryColorId;

      for (const key in this.patternObject) {
        if (
          this.patternObject[key].color1 === id ||
          this.patternObject[key].color2 === id
        ) {
          if (this.patternObject[key].color1 === id) {
            mainColorId = this.patternObject[key].color1;
            secondaryColorId = this.patternObject[key].color2;

            this.setProductData(mainColorId, secondaryColorId);
          } else {
            mainColorId = this.patternObject[key].color2;
            secondaryColorId = this.patternObject[key].color1;

            this.setProductData(mainColorId, secondaryColorId);
          }
          break;
        }
      }

      const mainIsShimmer = await this.isShimmer(mainColorId);
      return mainIsShimmer
        ? { mainColorId: secondaryColorId, secondaryColorId: mainColorId }
        : { mainColorId, secondaryColorId };
    },
    getProductSKU() {
      let sku = getValueForTag('Makeup SKU', this.product.tags);
      if (!sku) {
        sku = this.product.variants[0].sku;
      }
      return sku;
    },
  },
};
